import produce from 'immer';
import _ from 'lodash';
import { ReactNode, useCallback, useReducer } from 'react';
import UnreachableCaseError from '../utils/UnreachableCaseError';

export type ModalControl = {
  showModal: boolean;
  close: () => void;
  closeStart: () => void;
  closeFinish: () => void;
};

type Modal = {
  id: string;
  control: ModalControl;
  render(control: ModalControl): ReactNode;
};

type State = {
  modals: Record<string, Modal>;
};

const initialState: State = {
  modals: {},
};

type Action =
  | {
      type: 'add';
      id: string;
      control: ModalControl;
      render(control: ModalControl): ReactNode;
    }
  | {
      type: 'remove';
      id: string;
    }
  | {
      type: 'hide';
      id: string;
    };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'add':
      return produce(state, (draft) => {
        draft.modals[action.id] = {
          id: action.id,
          control: action.control,
          render: action.render,
        };
      });
    case 'remove':
      return produce(state, (draft) => {
        delete draft.modals[action.id];
      });
    case 'hide':
      return produce(state, (draft) => {
        draft.modals[action.id].control.showModal = false;
      });
    default:
      throw new UnreachableCaseError(action);
  }
}

export function useModalManagerState() {
  const [state, dispatch] = useReducer(reducer, initialState);

  const add = useCallback((render: (control: ModalControl) => ReactNode) => {
    const id = _.uniqueId();
    dispatch({
      type: 'add',
      id,
      control: {
        showModal: true,
        close: () => dispatch({ type: 'remove', id }),
        closeStart: () => dispatch({ type: 'hide', id }),
        closeFinish: () => dispatch({ type: 'remove', id }),
      },
      render,
    });
  }, []);

  return {
    add,
    ...state,
  };
}
